import React from 'react';
import { VStack, HStack, Box, Text, Skeleton, Stack, Separator } from '@chakra-ui/react';
import { BottomSheet } from '@/components/ui/custom/bottom-sheet';
import { useTransactionDetail } from '@/hooks/wallet/useTransactionDetail';
import {
  ScanIcon,
  ChargeIcon,
  DisplayIcon,
  DefaultIcon,
} from '@/components/transaction/transaction-icons';

interface TransactionDetailSheetProps {
  transactionCode: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  '00': 'チャージ',
  '01': '支払い',
  '02': '送金',
};

const typeIcon = (type: string, boxSize = 10) => {
  switch (type) {
    case '00':
      return <ChargeIcon boxSize={boxSize} />;
    case '01':
      return <ScanIcon boxSize={boxSize} />;
    case '02':
      return <DisplayIcon boxSize={boxSize} />;
    default:
      return <DefaultIcon boxSize={boxSize} />;
  }
};

const formatDatetime = (datetime: string) => {
  const d = new Date(datetime);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date} ${time}`;
};

export const TransactionDetailSheet: React.FC<TransactionDetailSheetProps> = ({
  transactionCode,
  isOpen,
  onClose,
}) => {
  const { data, isLoading, error } = useTransactionDetail(isOpen ? transactionCode : null);

  const amountColor = data ? (data.transactionType === '00' ? 'green.600' : 'red.600') : 'gray.800';

  const formattedAmount = data
    ? (() => {
        const prefix = data.totalAmount > 0 && data.transactionType !== '00' ? '-' : '+';
        return `${prefix}฿${Math.abs(data.totalAmount).toLocaleString()}`;
      })()
    : '';

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      title="決済詳細"
      height="70vh"
    >
      {isLoading && (
        <Stack gap={4}>
          <Skeleton height="80px" borderRadius="lg" />
          <Skeleton height="40px" borderRadius="lg" />
          <Skeleton height="120px" borderRadius="lg" />
          <Skeleton height="120px" borderRadius="lg" />
        </Stack>
      )}

      {error && (
        <Box textAlign="center" py={8}>
          <Text color="red.500" fontSize="sm">
            取引詳細の取得に失敗しました
          </Text>
          <Text color="gray.400" fontSize="xs" mt={1}>
            {error.message}
          </Text>
        </Box>
      )}

      {data && (
        <VStack gap={5} align="stretch">
          {/* アイコン & 種別 */}
          <VStack gap={1}>
            <Box>{typeIcon(data.transactionType)}</Box>
            <Text fontSize="sm" color="gray.500">
              {TYPE_LABELS[data.transactionType] ?? '不明'}
            </Text>
          </VStack>

          {/* タイトル & 金額 */}
          <VStack gap={1}>
            <Text fontSize="lg" fontWeight="bold" color="gray.800" textAlign="center">
              {data.storeName ?? data.title}
            </Text>
            <Text fontSize="3xl" fontWeight="extrabold" color={amountColor}>
              {formattedAmount}{' '}
              <Text as="span" fontSize="lg" color="gray.500">
                Bun
              </Text>
            </Text>
          </VStack>

          {/* 商品明細 */}
          {data.items && data.items.length > 0 && (
            <Box borderRadius="xl" bg="gray.50" p={4}>
              <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>
                商品明細
              </Text>
              <VStack gap={2} align="stretch">
                {data.items.map((item, i) => (
                  <HStack key={i} justify="space-between">
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm" color="gray.700">
                        {item.productName}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        ฿{item.unitPrice.toLocaleString()} × {item.quantity}
                      </Text>
                    </VStack>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      ฿{(item.unitPrice * item.quantity).toLocaleString()}
                    </Text>
                  </HStack>
                ))}
                <Separator />
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold" color="gray.700">
                    合計
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={amountColor}>
                    ฿{data.totalAmount.toLocaleString()}
                  </Text>
                </HStack>
              </VStack>
            </Box>
          )}

          {/* 取引情報 */}
          <Box borderRadius="xl" bg="gray.50" p={4}>
            <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>
              取引情報
            </Text>
            <VStack gap={3} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.500">
                  日時
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  {formatDatetime(data.transactionDate)}
                </Text>
              </HStack>
              <Separator />
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.500">
                  残高（取引後）
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  ฿{data.balanceAfter.toLocaleString()} Bun
                </Text>
              </HStack>
              <Separator />
              <HStack justify="space-between" align="start">
                <Text fontSize="sm" color="gray.500" flexShrink={0}>
                  取引コード
                </Text>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  fontFamily="mono"
                  textAlign="right"
                  wordBreak="break-all"
                  maxW="60%"
                >
                  {data.transactionCode}
                </Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      )}
    </BottomSheet>
  );
};
